import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import MessageIcon from "@mui/icons-material/Message";
import { useLocation } from "react-router-dom";
import config from "../Config/config";
import { auth } from "../Config/firebase";

const SILENCE_MS = 3000;
const AI_CHAT_HINT_DISMISSED_KEY = "explorerAiChatHintDismissed";

const INLINE_TOKEN_REGEX =
  /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\((https?:\/\/[^\s)]+)\))/g;

const makeAssistantWelcome = () => ({
  id: `assistant-welcome-${Date.now()}`,
  role: "assistant",
  content:
    "Hi, I am your Listernaut Explorer assistant. Ask me anything about Explorer features and workflows.",
  citations: [],
});

const renderInlineText = (text) => {
  const value = String(text || "");
  if (!value) return null;

  const tokens = value.split(INLINE_TOKEN_REGEX).filter(Boolean);
  return tokens.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      return (
        <strong key={`bold-${index}`} className="font-semibold text-slate-900">
          {part.slice(2, -2)}
        </strong>
      );
    }

    if (part.startsWith("`") && part.endsWith("`") && part.length > 2) {
      return (
        <code
          key={`code-${index}`}
          className="px-1 py-0.5 rounded bg-slate-200 text-[12px] text-slate-800"
        >
          {part.slice(1, -1)}
        </code>
      );
    }

    const linkMatch = part.match(/^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/);
    if (linkMatch) {
      return (
        <a
          key={`link-${index}`}
          href={linkMatch[2]}
          target="_blank"
          rel="noreferrer"
          className="text-blue-700 underline underline-offset-2 hover:text-blue-800"
        >
          {linkMatch[1]}
        </a>
      );
    }

    return <React.Fragment key={`txt-${index}`}>{part}</React.Fragment>;
  });
};

const parseAssistantBlocks = (content) => {
  const rawLines = String(content || "").replace(/\r/g, "").split("\n");
  const blocks = [];
  let paragraphLines = [];
  let listType = null;
  let listItems = [];

  const flushParagraph = () => {
    if (paragraphLines.length === 0) return;
    blocks.push({ type: "p", text: paragraphLines.join(" ") });
    paragraphLines = [];
  };

  const flushList = () => {
    if (!listType || listItems.length === 0) return;
    blocks.push({ type: listType, items: [...listItems] });
    listType = null;
    listItems = [];
  };

  for (const rawLine of rawLines) {
    const line = rawLine.trim();

    if (!line) {
      flushParagraph();
      flushList();
      continue;
    }

    const heading = line.match(/^#{1,3}\s+(.+)$/);
    if (heading) {
      flushParagraph();
      flushList();
      blocks.push({ type: "h", text: heading[1].trim() });
      continue;
    }

    const ordered = line.match(/^\d+[.)]\s+(.*)$/);
    if (ordered) {
      flushParagraph();
      if (listType !== "ol") {
        flushList();
        listType = "ol";
      }
      listItems.push(ordered[1]);
      continue;
    }

    const bullet = line.match(/^[-*•]\s+(.*)$/);
    if (bullet) {
      flushParagraph();
      if (listType !== "ul") {
        flushList();
        listType = "ul";
      }
      listItems.push(bullet[1]);
      continue;
    }

    if (listType && listItems.length > 0) {
      listItems[listItems.length - 1] = `${listItems[listItems.length - 1]} ${line}`.trim();
      continue;
    }

    paragraphLines.push(line);
  }

  flushParagraph();
  flushList();
  return blocks;
};

const renderAssistantContent = (content) => {
  const blocks = parseAssistantBlocks(content);
  if (blocks.length === 0) return null;

  return blocks.map((block, index) => {
    if (block.type === "h") {
      return (
        <h4
          key={`h-${index}`}
          className="text-[14px] font-semibold text-slate-900 mt-2 mb-1 first:mt-0"
        >
          {renderInlineText(block.text)}
        </h4>
      );
    }

    if (block.type === "p") {
      return (
        <p key={`p-${index}`} className="text-[13.5px] leading-6 text-slate-700 mb-2 last:mb-0">
          {renderInlineText(block.text)}
        </p>
      );
    }

    if (block.type === "ol") {
      return (
        <ol
          key={`ol-${index}`}
          className="list-decimal pl-5 text-[13.5px] leading-6 text-slate-700 space-y-1.5 mb-2 last:mb-0"
        >
          {block.items.map((item, itemIndex) => (
            <li key={`oli-${index}-${itemIndex}`}>{renderInlineText(item)}</li>
          ))}
        </ol>
      );
    }

    return (
      <ul
        key={`ul-${index}`}
        className="list-disc pl-5 text-[13.5px] leading-6 text-slate-700 space-y-1.5 mb-2 last:mb-0"
      >
        {block.items.map((item, itemIndex) => (
          <li key={`uli-${index}-${itemIndex}`}>{renderInlineText(item)}</li>
        ))}
      </ul>
    );
  });
};

const ExplorerAiChat = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState(() =>
    String(localStorage.getItem("userId") || auth.currentUser?.uid || "").trim()
  );
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(true);
  const [speechError, setSpeechError] = useState("");
  const [messages, setMessages] = useState([makeAssistantWelcome()]);

  const [supportOpen, setSupportOpen] = useState(false);
  const [supportSubmitting, setSupportSubmitting] = useState(false);
  const [supportError, setSupportError] = useState("");
  const [supportWarning, setSupportWarning] = useState("");
  const [supportResult, setSupportResult] = useState(null);
  const [supportForm, setSupportForm] = useState({
    name: String(localStorage.getItem("username") || "").trim(),
    email: String(localStorage.getItem("email") || "").trim(),
    phone: "",
    problem: "",
  });
  const [supportScreenshot, setSupportScreenshot] = useState(null);
  const [hintDismissed, setHintDismissed] = useState(
    () => localStorage.getItem(AI_CHAT_HINT_DISMISSED_KEY) === "true"
  );

  const recognitionRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const baseInputRef = useRef("");
  const finalTranscriptRef = useRef("");
  const manualStopRef = useRef(false);
  const shouldAutoSendRef = useRef(false);
  const latestInputRef = useRef("");
  const messagesEndRef = useRef(null);
  const loadedHistoryForUserRef = useRef("");

  const canSend = input.trim().length > 0 && !loading;

  useEffect(() => {
    latestInputRef.current = input;
  }, [input]);

  useEffect(() => {
    const cachedId = String(localStorage.getItem("userId") || "").trim();
    if (cachedId && cachedId !== userId) {
      setUserId(cachedId);
    }

    const unsub = auth.onAuthStateChanged((firebaseUser) => {
      const storedId = String(localStorage.getItem("userId") || "").trim();
      const firebaseId = String(firebaseUser?.uid || "").trim();
      const isAdminSwitchMode = localStorage.getItem("admin") === "true";

      // In admin switch mode, preserve the selected userId across reloads.
      // Otherwise, keep localStorage synced to the authenticated Firebase user.
      const nextId = isAdminSwitchMode ? (storedId || firebaseId) : (firebaseId || storedId);
      if (nextId && (!isAdminSwitchMode || !storedId)) {
        localStorage.setItem("userId", nextId);
      }
      if (nextId && nextId !== userId) {
        setUserId(nextId);
      }
    });

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (open && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, loading, open, historyLoading]);

  useEffect(() => {
    const handleOpenChat = () => setOpen(true);
    window.addEventListener("open-explorer-ai-chat", handleOpenChat);
    return () => window.removeEventListener("open-explorer-ai-chat", handleOpenChat);
  }, []);

  const hydrateSupportPrefill = async (uid) => {
    if (!uid) return;

    try {
      const response = await axios.get(`${config.API_BASE_URL}/get-user-details`, {
        params: { userId: uid },
      });

      const user = response.data || {};
      setSupportForm((prev) => ({
        ...prev,
        name: prev.name || String(user.username || "").trim(),
        email: prev.email || String(user.email || "").trim(),
        phone: prev.phone || String(user.phone || "").trim(),
      }));
    } catch {
      // ignore prefill fetch failures
    }
  };

  const loadChatHistory = async (uid) => {
    if (!uid || loadedHistoryForUserRef.current === uid) return;

    setHistoryLoading(true);
    try {
      const response = await axios.get(`${config.API_BASE_URL}/docs/ai-chat/history`, {
        params: {
          userId: uid,
          limit: 60,
        },
      });

      const history = Array.isArray(response.data?.messages)
        ? response.data.messages
        : [];

      if (history.length > 0) {
        setMessages(
          history.map((message, idx) => ({
            id: String(message.id || `${message.role}-${message.createdAtMs || idx}`),
            role: message.role === "assistant" ? "assistant" : "user",
            content: String(message.content || ""),
            citations: Array.isArray(message.citations) ? message.citations : [],
          }))
        );
      } else {
        setMessages([makeAssistantWelcome()]);
      }

      const profile = response.data?.profile || {};
      setSupportForm((prev) => ({
        ...prev,
        name: prev.name || String(profile.name || "").trim(),
        email: prev.email || String(profile.email || "").trim(),
        phone: prev.phone || String(profile.phone || "").trim(),
      }));

      loadedHistoryForUserRef.current = uid;
    } catch {
      setMessages((prev) => (prev.length > 0 ? prev : [makeAssistantWelcome()]));
    } finally {
      setHistoryLoading(false);
    }

    await hydrateSupportPrefill(uid);
  };

  useEffect(() => {
    loadChatHistory(userId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setSpeechSupported(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onstart = () => {
      manualStopRef.current = false;
      setIsListening(true);
      setSpeechError("");
    };

    recognition.onresult = (event) => {
      let finalText = "";
      let interimText = "";

      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const transcript = event.results[i]?.[0]?.transcript || "";
        if (event.results[i].isFinal) {
          finalText += `${transcript} `;
        } else {
          interimText += `${transcript} `;
        }
      }

      if (finalText.trim()) {
        finalTranscriptRef.current = `${finalTranscriptRef.current} ${finalText}`
          .replace(/\s+/g, " ")
          .trim();
      }

      const merged = [baseInputRef.current, finalTranscriptRef.current, interimText.trim()]
        .filter(Boolean)
        .join(" ")
        .replace(/\s+/g, " ")
        .trim();

      setInput(merged);

      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }

      silenceTimerRef.current = setTimeout(() => {
        if (!recognitionRef.current) return;
        shouldAutoSendRef.current = true;
        recognitionRef.current.stop();
      }, SILENCE_MS);
    };

    recognition.onerror = (event) => {
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }

      if (event.error === "not-allowed") {
        setSpeechError("Microphone permission denied.");
      } else if (event.error === "no-speech") {
        setSpeechError("No speech detected.");
      } else {
        setSpeechError("Microphone input failed. Please try again.");
      }

      shouldAutoSendRef.current = false;
      setIsListening(false);
    };

    recognition.onend = () => {
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }

      setIsListening(false);
      if (manualStopRef.current) {
        manualStopRef.current = false;
        shouldAutoSendRef.current = false;
        return;
      }

      if (shouldAutoSendRef.current) {
        shouldAutoSendRef.current = false;
        const textToSend = latestInputRef.current.trim();
        if (textToSend) {
          sendMessage(textToSend);
        }
      }
    };

    recognitionRef.current = recognition;

    return () => {
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }

      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch {
          // ignore stop errors on unmount
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendMessage = async (overrideMessage) => {
    const message = String(overrideMessage ?? input).trim();
    if (!message || loading) return;

    if (!userId) {
      setMessages((prev) => [
        ...prev,
        {
          id: `assistant-error-${Date.now()}`,
          role: "assistant",
          content: "Unable to identify your user account. Please refresh and log in again.",
          citations: [],
        },
      ]);
      return;
    }

    const userMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: message,
      citations: [],
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const response = await axios.post(`${config.API_BASE_URL}/docs/ai-chat`, {
        userId,
        message,
      });

      const assistantMessage = {
        id: `assistant-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        role: "assistant",
        content: response.data?.answer || "I could not generate a response.",
        citations: Array.isArray(response.data?.citations) ? response.data.citations : [],
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      const assistantError = {
        id: `assistant-error-${Date.now()}`,
        role: "assistant",
        content:
          error.response?.data?.error ||
          "I could not answer right now. Please try again.",
        citations: [],
      };
      setMessages((prev) => [...prev, assistantError]);
    } finally {
      setLoading(false);
    }
  };

  const toggleMic = async () => {
    if (!speechSupported || loading) return;

    if (isListening) {
      manualStopRef.current = true;
      shouldAutoSendRef.current = false;
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }
      recognitionRef.current?.stop();
      return;
    }

    try {
      setSpeechError("");
      baseInputRef.current = input.trim();
      finalTranscriptRef.current = "";
      shouldAutoSendRef.current = false;
      manualStopRef.current = false;
      recognitionRef.current?.start();
    } catch {
      setSpeechError("Unable to start microphone. Please try again.");
    }
  };

  const openSupportTicketModal = async () => {
    setSupportOpen(true);
    setSupportError("");
    setSupportWarning("");
    setSupportResult(null);
    if (userId) {
      await hydrateSupportPrefill(userId);
    }
  };

  const closeSupportTicketModal = () => {
    if (supportSubmitting) return;
    setSupportOpen(false);
    setSupportError("");
    setSupportWarning("");
    setSupportResult(null);
    setSupportScreenshot(null);
    setSupportForm((prev) => ({ ...prev, problem: "" }));
  };

  const onSupportInputChange = (field, value) => {
    setSupportForm((prev) => ({ ...prev, [field]: value }));
  };

  const submitSupportTicket = async () => {
    setSupportError("");
    setSupportWarning("");
    setSupportResult(null);

    if (!userId) {
      setSupportError("Unable to identify your user account. Please refresh and log in again.");
      return;
    }

    const name = String(supportForm.name || "").trim();
    const email = String(supportForm.email || "").trim();
    const phone = String(supportForm.phone || "").trim();
    const problem = String(supportForm.problem || "").trim();

    if (!name || !email || !phone || !problem) {
      setSupportError("Name, email, phone, and problem are required.");
      return;
    }

    setSupportSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("userId", userId);
      formData.append("name", name);
      formData.append("email", email);
      formData.append("phone", phone);
      formData.append("problem", problem);
      formData.append("source", "explorer-ai-chat");
      if (supportScreenshot) {
        formData.append("screenshot", supportScreenshot);
      }

      const response = await axios.post(`${config.API_BASE_URL}/docs/support-tickets`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSupportResult(response.data || null);
      if (response.data?.warning) {
        setSupportWarning(String(response.data.warning));
      }
      setSupportForm((prev) => ({ ...prev, problem: "" }));
      setSupportScreenshot(null);
    } catch (error) {
      setSupportError(
        error.response?.data?.error ||
          "Failed to create support ticket. Please try again."
      );
    } finally {
      setSupportSubmitting(false);
    }
  };

  const onKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const dismissHint = () => {
    setHintDismissed(true);
    localStorage.setItem(AI_CHAT_HINT_DISMISSED_KEY, "true");
  };

  const currentPath = String(location.pathname || "");
  const isManualPage = currentPath.startsWith("/manual");
  const isBulkPage = currentPath.startsWith("/bulk");
  const dockBottomPx = isBulkPage ? 182 : isManualPage ? 90 : 16;
  const dockStyle = {
    bottom: `calc(${dockBottomPx}px + env(safe-area-inset-bottom, 0px))`,
  };

  return (
    <>
      <div className="fixed right-4 z-[1200]" style={dockStyle}>
        {open ? (
          <div className="w-[88vw] max-w-[390px] sm:max-w-[420px] h-[62vh] sm:h-[68vh] max-h-[600px] rounded-2xl border border-slate-200 bg-white shadow-[0_22px_52px_rgba(15,23,42,0.28)] flex flex-col overflow-hidden">
            <div className="px-4 py-3 bg-gradient-to-r from-[#333999] to-[#5555bb] text-white flex items-center justify-between">
              <div>
                <div className="text-[13px] font-semibold tracking-wide">Explorer AI Assistant</div>
                <div className="text-[11px] text-indigo-100">Docs-powered help</div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-white/90 hover:text-white text-lg leading-none"
                aria-label="Close chat"
              >
                ×
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-3 py-3 bg-slate-50/70 space-y-3">
              {historyLoading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-slate-200 text-slate-600 rounded-2xl px-3 py-2 text-sm shadow-sm flex items-center gap-2">
                    <span className="inline-block h-2 w-2 rounded-full bg-slate-400 animate-pulse" />
                    Loading chat history...
                  </div>
                </div>
              )}

              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[90%] rounded-2xl px-3 py-2.5 ${
                      message.role === "user"
                        ? "bg-slate-900 text-white shadow-sm"
                        : "bg-white border border-slate-200 text-slate-800 shadow-sm"
                    }`}
                  >
                    {message.role === "assistant" ? (
                      <div className="border-l-2 border-indigo-100 pl-2">
                        {renderAssistantContent(message.content)}
                      </div>
                    ) : (
                      <div className="text-[14px] leading-6 whitespace-pre-wrap">
                        {message.content}
                      </div>
                    )}

                    {message.role === "assistant" &&
                      Array.isArray(message.citations) &&
                      message.citations.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-slate-200">
                          <p className="text-[10.5px] uppercase tracking-wide text-slate-500 mb-1">
                            Sources
                          </p>
                          <ul className="space-y-1">
                            {message.citations.slice(0, 3).map((cite, index) => (
                              <li
                                key={`${message.id}-cite-${index}`}
                                className="text-[11.5px] text-slate-600"
                              >
                                {cite.docTitle}
                                {cite.section ? ` • ${cite.section}` : ""}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-slate-200 text-slate-600 rounded-2xl px-3 py-2 text-sm shadow-sm flex items-center gap-2">
                    <span className="inline-block h-2 w-2 rounded-full bg-slate-400 animate-pulse" />
                    Thinking...
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="p-3 border-t border-slate-200 bg-white">
              <div className="mb-2 flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={openSupportTicketModal}
                  disabled={loading || historyLoading || !userId}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${
                    loading || historyLoading || !userId
                      ? "border-slate-200 text-slate-400 bg-slate-100 cursor-not-allowed"
                      : "border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100"
                  }`}
                >
                  Create Support Ticket
                </button>

                <p className="text-[11px] text-slate-500 text-right">
                  Chat is saved to your account
                </p>
              </div>

              <textarea
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={onKeyDown}
                rows={2}
                placeholder="Ask about Listernaut Explorer..."
                className="w-full resize-none rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
              />

              {speechError && <p className="mt-2 text-[11px] text-rose-600">{speechError}</p>}

              <div className="mt-2 flex items-center justify-end gap-2">
                {/* <button
                  type="button"
                  onClick={toggleMic}
                  disabled={!speechSupported || loading}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${
                    !speechSupported || loading
                      ? "border-slate-200 text-slate-400 bg-slate-100 cursor-not-allowed"
                      : isListening
                      ? "border-emerald-300 text-emerald-700 bg-emerald-50"
                      : "border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100"
                  }`}
                >
                  {isListening ? "Stop Mic" : "Use Mic"}
                </button> */}

                <p className="text-[11px] text-slate-500 text-right">
                  {isListening ? "Listening... sends 3s after silence" : "Press Enter to send"}
                </p>

                <button
                  type="button"
                  onClick={() => sendMessage()}
                  disabled={!canSend}
                  className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-all ${
                    canSend
                      ? "bg-slate-900 text-white hover:bg-slate-800"
                      : "bg-slate-200 text-slate-500 cursor-not-allowed"
                  }`}
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-end gap-2">
            {!hintDismissed && (
              <div className="max-w-[200px] sm:max-w-[260px] rounded-xl border border-slate-200 bg-white text-slate-700 shadow-[0_10px_24px_rgba(15,23,42,0.2)] px-2.5 sm:px-3 py-2 text-[11px] sm:text-xs leading-5">
                <div className="flex items-start justify-between gap-2">
                  <p>Hey, use this to understand how to catalog in Explorer.</p>
                  <button
                    type="button"
                    onClick={dismissHint}
                    className="text-slate-500 hover:text-slate-700 text-sm leading-none"
                    aria-label="Dismiss helper"
                  >
                    ×
                  </button>
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="h-11 w-11 sm:h-12 sm:w-12 rounded-full bg-gradient-to-r from-[#333999] to-[#5555bb] text-white shadow-[0_14px_32px_rgba(51,51,153,0.45)] hover:opacity-95 flex items-center justify-center"
              aria-label="Open Explorer AI chat"
            >
              <MessageIcon sx={{ fontSize: 22 }} />
            </button>
          </div>
        )}
      </div>

      {supportOpen && (
        <div className="fixed inset-0 z-[1300] bg-slate-900/50 backdrop-blur-[1px] flex items-center justify-center px-4">
          <div className="w-full max-w-[560px] rounded-2xl bg-white border border-slate-200 shadow-[0_24px_60px_rgba(15,23,42,0.32)] overflow-hidden">
            <div className="px-4 py-3 bg-gradient-to-r from-[#7c2d12] to-[#b45309] text-white flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold tracking-wide">Create Support Ticket</h3>
                <p className="text-[11px] text-amber-100">Includes your chat transcript up to now</p>
              </div>
              <button
                type="button"
                onClick={closeSupportTicketModal}
                className="text-white/90 hover:text-white text-lg leading-none"
                disabled={supportSubmitting}
              >
                ×
              </button>
            </div>

            <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[12px] font-semibold text-slate-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={supportForm.name}
                    onChange={(e) => onSupportInputChange("name", e.target.value)}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-400"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-slate-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={supportForm.email}
                    onChange={(e) => onSupportInputChange("email", e.target.value)}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-400"
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[12px] font-semibold text-slate-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={supportForm.phone}
                  onChange={(e) => onSupportInputChange("phone", e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-400"
                  placeholder="Required for support follow-up"
                />
              </div>

              <div>
                <label className="block text-[12px] font-semibold text-slate-700 mb-1">Problem</label>
                <textarea
                  rows={5}
                  value={supportForm.problem}
                  onChange={(e) => onSupportInputChange("problem", e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-400"
                  placeholder="Describe the issue you need help with"
                />
              </div>

              <div>
                <label className="block text-[12px] font-semibold text-slate-700 mb-1">Screenshot (optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setSupportScreenshot(e.target.files?.[0] || null)}
                  className="block w-full text-xs text-slate-600"
                />
                {supportScreenshot && (
                  <p className="mt-1 text-[11px] text-slate-500">Selected: {supportScreenshot.name}</p>
                )}
              </div>

              {supportError && <p className="text-[12px] text-rose-600">{supportError}</p>}
              {supportWarning && <p className="text-[12px] text-amber-700">{supportWarning}</p>}

              {supportResult?.ticketId && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                  <p className="text-[12px] font-semibold text-emerald-800">
                    Ticket created: {supportResult.ticketId}
                  </p>
                  {supportResult.ticketUrl && (
                    <a
                      href={supportResult.ticketUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[12px] text-emerald-700 underline underline-offset-2"
                    >
                      Open Ticket View
                    </a>
                  )}
                </div>
              )}
            </div>

            <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeSupportTicketModal}
                disabled={supportSubmitting}
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100"
              >
                Close
              </button>
              <button
                type="button"
                onClick={submitSupportTicket}
                disabled={supportSubmitting}
                className={`rounded-lg px-3 py-1.5 text-sm font-semibold text-white ${
                  supportSubmitting ? "bg-amber-400" : "bg-amber-600 hover:bg-amber-500"
                }`}
              >
                {supportSubmitting ? "Creating..." : "Create Ticket"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ExplorerAiChat;
